/**
 * Zustand store для конфигурации виджета
 */

import { create } from 'zustand';
import { LarteChatConfig, SupportedLanguage, DEFAULT_CONFIG, ContactsConfig } from '@/types/config.types';
import { DEFAULT_TRANSLATIONS, CONTACTS } from '@/constants/defaults';
import { detectBrowserLanguage } from '@/utils/helpers';

/**
 * Состояние конфигурации
 */
interface ConfigState {
  /** Полная конфигурация виджета */
  config: LarteChatConfig;
  
  /** Текущий язык */
  language: SupportedLanguage;
  
  /** Переводы для текущего языка */
  translations: Record<string, string>;
  
  /** Контакты (настраиваемые или дефолтные) */
  contacts: ContactsConfig;
}

/**
 * Действия для конфигурации
 */
interface ConfigActions {
  /** Установить конфигурацию */
  setConfig: (config: Partial<LarteChatConfig>) => void;
  
  /** Изменить язык */
  setLanguage: (language: SupportedLanguage) => void;

  /** Установить контакты (например из Supabase) */
  setContacts: (contacts: ContactsConfig) => void;
  
  /** Получить перевод по ключу */
  t: (key: string) => string;
}

/**
 * Тип всего store
 */
type ConfigStore = ConfigState & ConfigActions;

/**
 * Получает начальную конфигурацию из window.LarteChat
 */
function getInitialConfig(): LarteChatConfig {
  const windowConfig = (window as { LarteChat?: Partial<LarteChatConfig> }).LarteChat;
  
  return {
    ...DEFAULT_CONFIG,
    ...windowConfig,
    apiKey: windowConfig?.apiKey || '',
    baseURL: windowConfig?.baseURL || '',
  } as LarteChatConfig;
}

/**
 * Определяет начальный язык
 */
function getInitialLanguage(config: LarteChatConfig): SupportedLanguage {
  // 1. Язык из конфигурации
  if (config.language && config.language !== 'auto') {
    return config.language;
  }

  // 2. Автоопределение из HTML
  if (config.autoDetectLanguage) {
    return detectBrowserLanguage();
  }

  // 3. Дефолт — определяем по браузеру
  return detectBrowserLanguage();
}

/**
 * Получает переводы для языка
 */
function getTranslationsForLanguage(
  language: SupportedLanguage,
  customTranslations?: LarteChatConfig['translations']
): Record<string, string> {
  const defaultTranslations = DEFAULT_TRANSLATIONS[language] || DEFAULT_TRANSLATIONS.ru;
  const customLangTranslations = customTranslations?.[language] || {};

  return {
    ...defaultTranslations,
    ...customLangTranslations,
  } as Record<string, string>;
}

/**
 * Zustand store для конфигурации
 */
export const useConfigStore = create<ConfigStore>((set, get) => {
  const config = getInitialConfig();
  const language = getInitialLanguage(config);
  const translations = getTranslationsForLanguage(language, config.translations);
  
  // Контакты: используем из конфига или дефолтные
  const contacts = {
    ...CONTACTS,
    ...config.contacts,
  };

  return {
    // Начальное состояние
    config,
    language,
    translations,
    contacts,

    // Actions
    setConfig: (newConfig) => {
      set((state) => ({
        config: {
          ...state.config,
          ...newConfig,
        },
      }));
    },

    setLanguage: (newLanguage) => {
      const { config } = get();
      const newTranslations = getTranslationsForLanguage(newLanguage, config.translations);

      set({
        language: newLanguage,
        translations: newTranslations,
      });

      // Вызываем колбек если есть
      config.onLanguageChange?.(newLanguage);
    },

    setContacts: (newContacts) => {
      set({
        contacts: {
          ...CONTACTS,
          ...newContacts,
        },
      });
    },

    t: (key: string) => {
      const { translations } = get();
      return translations[key] || key;
    },
  };
});

